import { Typography as FoodsTypography } from "@foodpilot/foods";
import { t } from "i18next";
import { useMemo } from "react";
import { Link, TypographyOwnProps } from "@mui/material";
import { useCustomTheme } from "src/api/client-api/customTheme/useCustomTheme";
import { type TextSpacing, textSpacingAttributes, textSpacingSx } from "src/components/Puck/config/layoutTokens";

export type TypographyProps = {
    variant?: TypographyOwnProps["variant"];
    align?: "left" | "center" | "right" | "justify";
    color?: string;
    content: string;
    gutterBottom?: boolean;
    noWrap?: boolean;
    linkUrl?: string | null;
    spacing?: TextSpacing;
};

type PuckProps = {
    puck?: Record<string, unknown>;
    id?: string;
};

export type CombinedProps = TypographyProps & PuckProps;

export const Typography = (props: CombinedProps) => {
    const customTheme = useCustomTheme();
    const { content, variant, align, color, linkUrl, spacing } = props;

    const isTranslationKey = useMemo(() => {
        return (
            typeof content === "string" && content.trim() !== "" && !content.includes(" ") && !content.includes("\n")
        );
    }, [content]);

    const displayContent = useMemo(() => {
        return isTranslationKey ? t(content, content) : content;
    }, [isTranslationKey, content]);

    const _linkUrl = linkUrl?.startsWith("http") ? linkUrl : `https://${linkUrl}`;

    const _color = customTheme?.customThemeConfig?.config?.palette?.primary?.[500] || color;

    const text = (
        <FoodsTypography
            variant={variant}
            color={color}
            align={align}
            sx={{
                textAlign: align,
                whiteSpace: "break-spaces",
                overflowWrap: "anywhere",
                ...textSpacingSx(spacing),
            }}
            dangerouslySetInnerHTML={{ __html: displayContent }}
            {...textSpacingAttributes(spacing)}
        />
    );

    if (!linkUrl) return text;

    return (
        <Link
            href={_linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            {...textSpacingAttributes(spacing)}
            sx={{
                wordBreak: "break-all",
                textDecoration: "underline",
                color: _color,
                "&:visited": {
                    color: _color,
                },
                "&:hover": {
                    color: _color,
                },
            }}
        >
            {text}
        </Link>
    );
};

Typography.displayName = "Typography";
